import type { RowParity } from './row-details-types.js';
import {
  DATA_TABLE_DETAILS_ROW_PARITY,
  DATA_TABLE_DETAILS_ROW_IDENTIFIER,
  DATA_TABLE_ROW_EXPANDED_ID_SUFFIX,
} from '../../constants.js';
import type { RowModel, Row, Table } from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import { memo } from '../../tanstack-table-core-override/src/index.js';

export function getRowDetailsRowModel<TData extends DataTableRowData>(): (
  table: Table<TData>,
) => () => RowModel<TData> {
  return (table) => {
    return memo(
      () => [table.getState().rowDetails, table.getPreRowDetailsRowModel()],
      (_rowDetails, rowModel) => {
        if (!rowModel.rows.length) {
          return rowModel;
        }

        const { rows, flatRows, rowsById } = rowModel;

        const detailsRowModel = expandRowDetails({
          rows,
          flatRows,
          rowsById,
        });

        return detailsRowModel;
      },
      getMemoOptions(table.options, 'debugRows', 'rowDetailsRowModel'),
    );
  };
}

function expandRowDetails<TData extends DataTableRowData>(
  rowModel: RowModel<TData>,
) {
  const detailsRows: Row<TData>[] = [];

  rowModel.rows.forEach((row, index) => {
    const parity: RowParity = index % 2 === 0 ? 'even' : 'odd';
    row[DATA_TABLE_DETAILS_ROW_PARITY] = parity;
    detailsRows.push(row);
    if (row.getIsDetailsOpen()) {
      const clonedRow: Row<TData> = Object.assign(row.clone(), {
        id: `${row.id}${DATA_TABLE_ROW_EXPANDED_ID_SUFFIX}`,
        [DATA_TABLE_DETAILS_ROW_IDENTIFIER]: true,
        // Make sure that the cloned row will not be selectable.
        getCanSelect: () => false,
        // Make sure that the cloned row will not show up in the subrows
        getCanExpand: () => false,
        // Make sure that the cloned row will be activateable
        getCanActivateRow: () => false,
        // Delegate to the original row so the aria-controls id matches the button's aria-controls.
        getRowDetailsAriaControlsId: () => row.getRowDetailsAriaControlsId(),
      });
      detailsRows.push(clonedRow);
      // Add the cloned row the the byId mapping as well, so that it can be looked up
      rowModel.rowsById[clonedRow.id] = clonedRow;
    }
  });

  return {
    rows: detailsRows,
    flatRows: rowModel.flatRows,
    rowsById: rowModel.rowsById,
  };
}
